import { useState, useEffect, useCallback } from "react";

import { lightOrDark, Shade } from "utils/colorUtils";

const normalizeColor = (color: string): string => `#${color.replace("#", "")}`;

interface IUseColorState {
  color: string;
  shade: Shade;
  setColor: (color: string) => void;
  handleColorChange: (color: string) => void;
  resetToDefault: () => void;
}

/** Tracks the color being edited (normalized to `#hex`) + its light/dark shade. */
export const useColorState = (
  defaultColor: string,
  isActive: boolean,
): IUseColorState => {
  const [color, setColorState] = useState(() => normalizeColor(defaultColor));
  const [shade, setShade] = useState<Shade>(
    () => lightOrDark(normalizeColor(defaultColor)) || "light",
  );

  useEffect(() => {
    if (isActive) {
      const normalized = normalizeColor(defaultColor);
      setColorState(normalized);
      setShade(lightOrDark(normalized) || "light");
    }
  }, [isActive, defaultColor]);

  const setColor = useCallback((newColor: string) => {
    const normalized = normalizeColor(newColor);
    setColorState(normalized);
    setShade(lightOrDark(normalized) || "light");
  }, []);

  const resetToDefault = useCallback(() => {
    const normalized = normalizeColor(defaultColor);
    setColorState(normalized);
    setShade(lightOrDark(normalized) || "light");
  }, [defaultColor]);

  return {
    color,
    shade,
    setColor,
    handleColorChange: setColor,
    resetToDefault,
  };
};

/** Read-only shade derived from a color. */
export const useShadeFromColor = (color: string): Shade => {
  const [shade, setShade] = useState<Shade>(
    () => lightOrDark(`#${color.replace("#", "")}`) || "light",
  );

  useEffect(() => {
    setShade(lightOrDark(`#${color.replace("#", "")}`) || "light");
  }, [color]);

  return shade;
};
